import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { CASH_MOVEMENTS_QUERY } from './cash-shifts.gql';

import { CASH_MOVEMENT_KIND_FROM_GQL } from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';

interface CashMovement {
  id: string;
  cash_shift_id: string;
  kind: string;
  amount_cents: string;
  reason: string;
  folio_number: string | null;
  status: 'recorded' | 'cancelled';
  created_at: string;
}

interface MovementsResponse {
  cashMovements: { items: CashMovement[]; page: { total: number } };
}

interface CashMovementsListProps {
  shift_id: string;
}

export function CashMovementsList({ shift_id }: CashMovementsListProps): JSX.Element {
  const { t } = useTranslation();
  const { data } = useQuery<MovementsResponse>(CASH_MOVEMENTS_QUERY, {
    variables: { cash_shift_id: shift_id, offset: 0, limit: 20 },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30_000,
  });
  const items = (data?.cashMovements.items ?? []).map((item) => ({
    ...item,
    kind:
      CASH_MOVEMENT_KIND_FROM_GQL[item.kind as keyof typeof CASH_MOVEMENT_KIND_FROM_GQL] ??
      item.kind,
  }));

  if (items.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">{t('cash_shifts.no_movements')}</p>
    );
  }
  return (
    <ul className="divide-y divide-border rounded-md border border-border text-sm">
      {items.map((m) => (
        <li key={m.id} className="flex items-center justify-between gap-2 px-3 py-2">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <Badge variant={m.kind === 'cash_in' ? 'success' : m.kind === 'cash_out' ? 'destructive' : 'muted'}>
                {t(`cash_shifts.kind_${m.kind}`)}
              </Badge>
              <span className="text-xs text-muted-foreground">{fmtTime(m.created_at)}</span>
              {m.folio_number ? (
                <span className="font-mono text-xs text-muted-foreground">#{m.folio_number}</span>
              ) : null}
            </div>
            <span className="text-xs">{m.reason}</span>
          </div>
          <span
            className={`font-mono tabular-nums ${
              m.kind === 'cash_out' ? 'text-destructive' : 'text-success'
            }`}
          >
            {m.kind === 'cash_out' ? '-' : ''}
            {formatCentsString(m.amount_cents)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
